/**
 * AiMessageBubble — renders a single chat message.
 *
 * Practitioner messages align right with a teal background.
 * AI messages align left with the lightest secondary background.
 * Timestamps are shown below each bubble.
 */

import React from "react";
import { Box, Typography } from "@mui/material";
import { SmartToyOutlined, PersonOutlined } from "@mui/icons-material";
import { AiMessage } from "./assessmentAi.mock";

type AiMessageBubbleProps = {
    message: AiMessage;
};

function formatTime(iso: string): string {
    return new Date(iso).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
    });
}

export const AiMessageBubble = ({ message }: AiMessageBubbleProps): JSX.Element => {
    const isPractitioner = message.role === "practitioner";

    return (
        <Box
            display="flex"
            flexDirection={isPractitioner ? "row-reverse" : "row"}
            alignItems="flex-start"
            gap={1}
            mb={2}
        >
            {/* Avatar icon */}
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    bgcolor: isPractitioner ? "primary.main" : "secondary.lightest",
                    flexShrink: 0,
                    mt: 0.5,
                }}
            >
                {isPractitioner ? (
                    <PersonOutlined sx={{ fontSize: "1rem", color: "common.white" }} />
                ) : (
                    <SmartToyOutlined sx={{ fontSize: "1rem", color: "secondary.main" }} />
                )}
            </Box>

            {/* Bubble + timestamp */}
            <Box
                maxWidth="75%"
                display="flex"
                flexDirection="column"
                alignItems={isPractitioner ? "flex-end" : "flex-start"}
            >
                <Box
                    px={2}
                    py={1.5}
                    sx={{
                        borderRadius: isPractitioner ? "16px 4px 16px 16px" : "4px 16px 16px 16px",
                        bgcolor: isPractitioner ? "primary.main" : "secondaryShade.main",
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{ color: isPractitioner ? "common.white" : "text.primary", lineHeight: 1.6 }}
                    >
                        {message.text}
                    </Typography>
                </Box>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, px: 0.5 }}>
                    {formatTime(message.timestamp)}
                </Typography>
            </Box>
        </Box>
    );
};
